import React from 'react';
import { motion } from 'framer-motion';

const LoadingSpinner = ({ size = 'md', variant = 'default', text = '' }) => {
  // Size configurations
  const sizes = {
    sm: { container: 'w-8 h-8', dot: 'w-2 h-2' },
    md: { container: 'w-12 h-12', dot: 'w-3 h-3' },
    lg: { container: 'w-16 h-16', dot: 'w-4 h-4' },
    xl: { container: 'w-20 h-20', dot: 'w-5 h-5' }
  };

  const sizeConfig = sizes[size] || sizes.md;

  // Variant configurations
  if (variant === 'dots') {
    return (
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="flex gap-2">
          {[0, 1, 2].map((index) => (
            <motion.div
              key={index}
              className={`${sizeConfig.dot} bg-blue-600 rounded-full`}
              animate={{
                y: [0, -12, 0],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 0.6,
                repeat: Infinity,
                delay: index * 0.15,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
        {text && <p className="text-sm text-gray-600 font-medium">{text}</p>}
      </div>
    );
  }

  if (variant === 'pulse') {
    return (
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="relative">
          {[0, 1, 2].map((index) => (
            <motion.div
              key={index}
              className={`${sizeConfig.container} absolute inset-0 border-4 border-blue-600 rounded-full`}
              initial={{ scale: 0, opacity: 1 }}
              animate={{
                scale: [0, 1.5],
                opacity: [1, 0]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: index * 0.5,
                ease: "easeOut"
              }}
            />
          ))}
          <div className={`${sizeConfig.container} bg-blue-600 rounded-full`} />
        </div>
        {text && <p className="text-sm text-gray-600 font-medium">{text}</p>}
      </div>
    );
  }

  if (variant === 'orbit') {
    return (
      <div className="flex flex-col items-center justify-center gap-4">
        <div className={`${sizeConfig.container} relative`}>
          {[0, 1, 2, 3].map((index) => (
            <motion.div
              key={index}
              className={`absolute ${sizeConfig.dot} bg-blue-600 rounded-full`}
              style={{
                left: '50%',
                top: '50%',
                marginLeft: `-${parseInt(sizeConfig.dot.split('w-')[1]) / 2}`,
                marginTop: `-${parseInt(sizeConfig.dot.split('h-')[1]) / 2}`
              }}
              animate={{
                rotate: 360
              }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: "linear",
                delay: index * 0.3
              }}
            >
              <motion.div
                className={`${sizeConfig.dot} bg-blue-600 rounded-full`}
                animate={{
                  x: size === 'sm' ? 12 : size === 'md' ? 16 : size === 'lg' ? 20 : 24
                }}
              />
            </motion.div>
          ))}
        </div>
        {text && <p className="text-sm text-gray-600 font-medium">{text}</p>}
      </div>
    );
  }

  if (variant === 'spinner') {
    return (
      <div className="flex flex-col items-center justify-center gap-4">
        <motion.div
          className={`${sizeConfig.container} border-4 border-gray-200 border-t-blue-600 rounded-full`}
          animate={{ rotate: 360 }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        {text && <p className="text-sm text-gray-600 font-medium">{text}</p>}
      </div>
    );
  }

  // Default: Modern gradient spinner
  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className="relative">
        <motion.div
          className={`${sizeConfig.container} rounded-full bg-gradient-to-tr from-blue-600 via-blue-400 to-mint`}
          animate={{ rotate: 360 }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className={`${sizeConfig.container} absolute inset-0 rounded-full bg-white`}
          style={{ margin: '4px' }}
          animate={{
            scale: [1, 0.95, 1],
            opacity: [0.9, 1, 0.9]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>
      {text && <p className="text-sm text-gray-600 font-medium">{text}</p>}
    </div>
  );
};

export default LoadingSpinner;
